





















































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState, mapGetters } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { InvoiceCategoryCode } from "@/models/invoices";

// Types
import { IOrderTemplate, IOrderTemplateForm } from "@/models/orderTemplates";
import { IState } from "@/store";
import { IInvoice } from "@/models/invoices";

@Component({
  components: {
    "discount-and-conditions": () =>
      import(
        "@/components/app/admin/catalogue/promotions/discountAndConditions.vue"
      ),
    "invoice-items": () =>
      import("@/components/app/global/invoices/invoiceItems.vue")
  },
  computed: {
    ...mapState({
      invoice(state: IState, getters): IInvoice {
        return getters["data/single"]({
          id: this.invoiceId,
          scope: this.invoiceScope(this.invoiceId),
          storeModule: DataModules.INVOICES
        });
      }
    }),
    ...mapGetters({
      invoiceScope: `data/${DataModules.INVOICES}/selectorScope`
    })
  }
})
export default class ManageOrderTemplateModal extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Refs

  public declare $refs: {
    recurringCount: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Map State

  public invoice!: IInvoice;

  // Map Getters

  public invoiceScope!: Function;

  // Props

  @Prop({ default: null })
  public orderTemplate!: IOrderTemplate;

  @Prop({ default: null })
  public orderId!: IInvoice["id"];

  // Variables

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isLoadingNewInvoice = false;
  public isProcessing = false;

  public form: IOrderTemplateForm = {
    name: "",
    code: "",
    recurring_count: 0,
    invoice_id: ""
  };
  public initialForm = {} as IOrderTemplateForm;

  public recurringCount = "non_recurring";
  public savedRecurringCount = 1;

  public invoiceId = this.$_.get(
    this.orderTemplate,
    "invoice_id",
    this.orderId
  );

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    if (!this.invoiceId) return;
    this.binInvoice(this.invoiceId);
  }

  // Methods

  public async loadData() {
    try {
      await this.getInvoiceData(this.isReloading);
      this.initialiseForm();

      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.isLoadingNewInvoice = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getInvoiceData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/get`, {
      id: this.invoiceId,
      scope: this.invoiceScope(this.invoiceId),
      params: {
        with: [
          "client",
          "client.image",
          "products",
          "products.product.image"
        ].join(",")
      },
      vm: this,
      ignoreStored
    });
  }

  public binInvoice(oldInvoiceId) {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.INVOICES,
      scope: this.invoiceScope(oldInvoiceId),
      vm: this
    });
  }

  public initialiseForm() {
    if (this.isUpdate) {
      this.form = this.$_.merge(
        {},
        this.form,
        this.$_.pick(this.orderTemplate, [
          "name",
          "code",
          "recurring_count",
          "invoice_id"
        ])
      );

      this.recurringCount =
        this.form.recurring_count === 0 ? "non_recurring" : "multiple";
    } else {
      this.$set(this.form, "invoice_id", this.orderId);
    }

    this.initialForm = this.$_.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      this.isUpdate
        ? await this.updateOrderTemplate()
        : await this.createOrderTemplate();

      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public createOrderTemplate() {
    return this.$store.dispatch(`data/${DataModules.ORDERS_TEMPLATES}/create`, {
      data: this.form
    });
  }

  public updateOrderTemplate() {
    return this.$store.dispatch(`data/${DataModules.ORDERS_TEMPLATES}/update`, {
      templateId: this.orderTemplate.id,
      data: this.form
    });
  }

  public onRecurringCountChange(value: any) {
    if (value === "non_recurring") {
      this.savedRecurringCount = this.form.recurring_count;
      this.form.recurring_count = 0;
      return;
    }

    this.form.recurring_count = this.savedRecurringCount;

    this.$nextTick(() => {
      this.$refs.recurringCount.focus();
    });
  }

  public openSelectOrderModal() {
    if (this.isDisabled || this.isLoadingNewInvoice) return;

    this.$store.dispatch(
      `data/${DataModules.ORDERS_TEMPLATES}/openSelectOrderModal`,
      {
        props: {
          selectedOrderId: this.form.invoice_id,
          manualFilters: {
            "filter[category.slug]": [
              InvoiceCategoryCode.NEW_CONTRACT,
              InvoiceCategoryCode.ADDITIONAL_SERVICE,
              InvoiceCategoryCode.ONE_TIME_SERVICE,
              InvoiceCategoryCode.MIGRATION,
              InvoiceCategoryCode.RECURRENT
            ].join(",")
          }
        },
        events: {
          success: selectedOrderId => {
            this.form.invoice_id = selectedOrderId;
            this.invoiceId = selectedOrderId;
            this.isLoadingNewInvoice = true;
            this.loadData();
          }
        }
      }
    );
  }

  // Getters

  public get isDisabled() {
    return false;
  }

  public get formIsDisabled() {
    return this.isProcessing || !this.formIsValid || this.hasErrors;
  }

  public get formIsValid() {
    if (!this.$_.isEqual(this.form, this.initialForm)) return true;
    return false;
  }

  public get isUpdate() {
    return !this.$_.isNull(this.orderTemplate);
  }

  public get title() {
    if (!this.isUpdate) return this.$t("_.create_order_template");
    return this.$t("_.update_order_template");
  }

  public get newValues() {
    const newValues: any = this.$_.omitBy(this.form, (v, k) => {
      return this.initialForm[k] === v;
    });

    return newValues;
  }

  public get isBasket() {
    return this.$_.isNull(this.$_.get(this.invoice, "number", null));
  }

  public get recurringCountOptions() {
    return [
      {
        leftLabel: this.$t("_.dont_pre_generate_invoices"),
        value: "non_recurring"
      },
      {
        leftLabel: this.$t("_.pre_generate_invoices"),
        value: "multiple"
      }
    ];
  }

  // Guarding

  public guardFunctionalities = ["staff_get_invoice"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
